import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";

import StorageIcon from "@mui/icons-material/Storage";
import RefreshIcon from "@mui/icons-material/Refresh";

const PageHeader = ({
  title,
  subtitle,

  // Optional icon
  icon: Icon = StorageIcon,

  // Optional right-side content
  statusLabel,
  statusValue,
  statusChip,

  // Optional refresh
  showRefresh = false,
  onRefresh,
  refreshing = false,

  // Optional customization
  headerGradient = "linear-gradient(135deg, #1976d2, #42a5f5)",
  iconGradient = "linear-gradient(135deg, #1976d2, #42a5f5)",
  iconColor = "#ffffff",
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        mb: 3,
        p: {
          xs: 2.5,
          md: 3.5,
        },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background:
          "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background */}
      <Box
        sx={{
          position: "absolute",
          right: -50,
          top: -60,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background:
            "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
          pointerEvents: "none",
        }}
      />

      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={2.5}
        alignItems={{
          xs: "flex-start",
          sm: "center",
        }}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Header Icon */}
        {Icon && (
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: iconGradient,
              color: iconColor,
              boxShadow: "0 8px 20px rgba(25,118,210,0.25)",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 34 }} />
          </Box>
        )}

        {/* Title / Subtitle */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              fontSize: {
                xs: "1.65rem",
                md: "2rem",
              },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 700,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Right Side */}
        {(statusLabel ||
          statusValue ||
          statusChip ||
          showRefresh) && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              width: {
                xs: "100%",
                sm: "auto",
              },
              justifyContent: {
                xs: "space-between",
                sm: "flex-end",
              },
            }}
          >
            {/* Status */}
            {(statusLabel || statusValue || statusChip) && (
              <Box
                sx={{
                  minWidth: {
                    xs: 0,
                    sm: 170,
                  },
                  textAlign: {
                    xs: "left",
                    sm: "right",
                  },
                }}
              >
                {statusLabel && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                    }}
                  >
                    {statusLabel}
                  </Typography>
                )}

                {statusValue && (
                  <Typography
                    variant="h6"
                    color="primary"
                    fontWeight={700}
                    sx={{
                      mt: 0.3,
                    }}
                  >
                    {statusValue}
                  </Typography>
                )}

                {statusChip && (
                  <Chip
                    label={statusChip.label}
                    size="small"
                    color={statusChip.color || "default"}
                    variant={statusChip.variant || "outlined"}
                    sx={{
                      mt: 0.5,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            )}

            {/* Refresh */}
            {showRefresh && onRefresh && (
              <Tooltip title={refreshing ? "Refreshing..." : "Refresh"}>
                <span>
                  <IconButton
                    onClick={onRefresh}
                    disabled={refreshing}
                    sx={{
                      width: 42,
                      height: 42,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "#ffffff",
                      color: "primary.main",

                      "&:hover": {
                        backgroundColor: "primary.50",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <RefreshIcon
                      sx={{
                        animation: refreshing
                          ? "spin 1s linear infinite"
                          : "none",

                        "@keyframes spin": {
                          from: {
                            transform: "rotate(0deg)",
                          },
                          to: {
                            transform: "rotate(360deg)",
                          },
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default PageHeader;